import React, { Suspense } from 'react'
import c from 'classnames'
import { connect } from 'react-redux'
import { getActiveQuery, getActiveParams } from 'selectors/pageStateSelectors'
import { toggleQueryValue, removeFromPageState } from 'actions/pageStateActions'

import Modal, { ModalBody, ModalControls, ModalClose } from 'discada/dist/Modal'
import Loading from 'discada/dist/Loading'
import Button from 'discada/dist/Button'
import { Controls } from 'discada/dist/Form'

import VRP from 'components/VRP'
import Rebates from 'components/Quote/Rebates'
import Trades from 'components/Quote/Trades'
import GenericAdditions from 'components/Quote/Additions'
import QV from 'components/Quote/QuoteValue'
import ProgramDetails from 'components/Quote/ProgramDetails'
import { INTENTS } from 'reducers/quoteReducers'

import StateFeeTaxAutoFormModal from 'components/widgets/StateFeeTaxAutoFormModal'

import AddVehicleForm from 'components/forms/AddVehicleForm'
import EditUserForm from 'components/forms/EditUserForm'
import SalesLeadForm from 'components/forms/SalesLeadForm'
import Warranty from 'components/VRP/Warranty'
import ProgramInfo from 'components/Quote/ProgramInfo'
import Slider from 'components/Quote/SimpleQuote/Slider'

import AddConfigForm from 'components/forms/AddConfigForm'
import { InventoryFilters } from 'pages/inventory/InventoryFilters'
import { getVehiclesForVLP } from 'actions/inventoryActions'
import { deleteConfig } from 'actions/appStatusActions'

import QuoteSaveForm from 'components/forms/QuoteSaveForm'
import { getProgramsForQuote } from 'actions/quoteActions'

import VehicleSettings from 'components/Settings/Vehicle'
import BankSettings from 'components/Settings/Bank'
import Integrations from 'components/Settings/Integrations'

import ChooseYourOwnAdventure from 'components/ChooseYourOwnAdventure'

import './Modals.scss'

const MagicLinker = React.lazy(() => import('components/widgets/MagicLinker'))
const PaymentIQ = React.lazy(() => import('components/widgets/PaymentIQ'))
const ShowAndTell = React.lazy(() => import('components/widgets/ShowAndTell'))
const LeadDetails = React.lazy(() => import('components/Lead'))

const Scripts = React.lazy(() => import('components/Settings/Scripts'))

const FiltersCloseSection = onClick => (
  <div className="close-section">
    <Button primary onClick={onClick}>
      Search
    </Button>
  </div>
)

const ModalSubmitSet = ({ onSave, onCancel, children, saveLabel }) => {
  return (
    <ModalControls>
      {onSave && (
        <Button success onClick={onSave}>
          {saveLabel || 'Apply to Your Quote'}
        </Button>
      )}
      {onCancel && (
        <Button text onClick={onCancel}>
          Close
        </Button>
      )}
      {children}
    </ModalControls>
  )
}

export function Modals({
  query,
  params,
  removeFromPageState,
  getVehiclesForVLP,
  getProgramsForQuote,
  deleteConfig,
}) {
  let r = params => removeFromPageState(params, true)

  const closeFiltersModal = () => {
      getVehiclesForVLP()
      r(['showInventoryFilters', 'filterGroup'])
    },
    closeUserModal = () => r(['addUser', 'editUserId'])

  let onVRPClose = () =>
      r(['vehicleId', 'quoteId', 'dealtuner.section', 'customerView']),
    closeQuoteModal = (refresh = true) => {
      r([
        'changeZip',
        'addRebate',
        'addTrade',
        'showFandI',
        'contractDetails',
        'printModal',
        'autoPrint',
      ])

      refresh &&
        setTimeout(
          () =>
            getProgramsForQuote(params.id || query.vehicleId, query.quoteId),
          1000,
        )
    }

  return (
    <div className="modals-lot">
      {!!query.vehicleId && (
        <React.Fragment>
          <Modal onClose={onVRPClose} className="vrp-modal vrp-fixed-header">
            <VRP
              noswitch
              showHeader
              id={query.vehicleId}
              quoteId={query.quoteId}
            />
          </Modal>
        </React.Fragment>
      )}

      {query.showTaxConfig && (
        <Modal
          onClose={() => r(['showTaxConfig'])}
          className="auto-width state-fee-tax-modal"
        >
          <ModalBody>
            <StateFeeTaxAutoFormModal
              onClose={() => r(['showTaxConfig'])}
              quoteId={query.quoteId}
              vehicleId={params.id}
            />
          </ModalBody>
        </Modal>
      )}

      {query.showRooftopModal && (
        <Modal
          onClose={() => r(['showRooftopModal'])}
          className="auto-width rooftop-select-modal"
        >
          <ModalBody>
            <div className="container">
              <div className="columns">
                <div className="column">
                  <h1 className="is-size-3">Where would you like to go?</h1>
                  <ChooseYourOwnAdventure />
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>
      )}

      {query.leadId && (
        <Modal
          className={c('lead-modal no-padding')}
          onClose={() => r(['leadId'])}
        >
          <ModalBody>
            <Suspense fallback={<Loading />}>
              <LeadDetails id={query.leadId} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {(query.addConfig || query.cloneConfig) && (
        <Modal
          onClose={() => r(['addConfig', 'cloneConfig'])}
          className="auto-width inventory-filter-modal"
        >
          <ModalBody>
            <AddConfigForm
              onCancel={() => r(['addConfig', 'cloneConfig'])}
              category={query.addConfig}
              cloneId={query.cloneConfig}
            />
          </ModalBody>
        </Modal>
      )}

      {query.deleteConfig && (
        <Modal onClose={() => r(['deleteConfig'])} className="top-level">
          <ModalBody>
            <article className={c('message', 'is-danger')}>
              <div className="message-body">
                <h4>
                  Any cars that are using these settings to generate pricing
                  will fallback to the next best matching settings.
                  <br />
                  Deleting this config can't be undone.
                </h4>
              </div>
            </article>

            <Controls
              saveLabel="Delete Settings"
              onSave={() => deleteConfig(query.deleteConfig)}
              onCancel={() => r(['deleteConfig'])}
              danger
            />
          </ModalBody>
        </Modal>
      )}

      {query.addVehicle && (
        <Modal onClose={() => r(['addVehicle'])}>
          <ModalBody>
            <AddVehicleForm onCancel={() => r(['addVehicle'])} />
          </ModalBody>
        </Modal>
      )}

      {query.addRebate && (
        <Modal
          onClose={closeQuoteModal}
          noClose
          className="inside-close rebates-modal"
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={closeQuoteModal}
            />
            <Rebates {...query} vehicleId={params.id} hideTitle />
          </ModalBody>
          <ModalSubmitSet onSave={closeQuoteModal} onCancel={closeQuoteModal} />
        </Modal>
      )}

      {query.addTrade && (
        <Modal onClose={closeQuoteModal} noClose className="inside-close">
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={closeQuoteModal}
            />
            <br />
            <br />
            <Trades {...query} vehicleId={params.id} />
          </ModalBody>
          <ModalSubmitSet onSave={closeQuoteModal} onCancel={closeQuoteModal} />
        </Modal>
      )}

      {query.contractDetails && (
        <Modal onClose={() => closeQuoteModal(false)}>
          <ModalBody>
            <ProgramInfo
              {...query}
              key="modal-info"
              vehicleId={params.id}
              mode={query.contractDetails}
            />
          </ModalBody>
          <ModalSubmitSet onCancel={() => closeQuoteModal(false)}>
            <Button text onClick={() => window.print()}>
              Print&hellip;
            </Button>
          </ModalSubmitSet>
        </Modal>
      )}

      {query.showFandI && (
        <Modal noClose className="inside-close quote-fandi">
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['showFandI'])}
            />
            <Warranty
              id={params.id}
              quoteId={query.quoteId}
              mode={query.showFandI}
              header="Included Coverage"
            />

            <h1>Select Addons and Extras</h1>
            <GenericAdditions
              {...query}
              title="Additions"
              addsKey="additions"
            />
            <GenericAdditions
              {...query}
              title="Warranties"
              addsKey="warranties"
            />
            <GenericAdditions
              {...query}
              title="Maintenance Plans"
              addsKey="maintenancePlans"
            />
          </ModalBody>
          <ModalSubmitSet onSave={closeQuoteModal} onCancel={closeQuoteModal} />
        </Modal>
      )}

      {query.quoteAction && query.mode && (
        <Modal noClose className="inside-close quote-action-modal">
          <ModalBody>
            {query.quoteAction !== INTENTS.contact && (
              <ModalClose
                classaName="filter-close-button"
                onClose={() => r(['quoteAction', 'mode'])}
              />
            )}
            <QuoteSaveForm
              vehicleId={params.id}
              {...query}
              intent={query.quoteAction}
              onCancel={() => r(['quoteAction', 'mode'])}
              autoSelect
            />
          </ModalBody>
        </Modal>
      )}

      {query.changeZip && (
        <Modal className="auto-width zip-code-modal" onClose={closeQuoteModal}>
          <ModalBody>
            <h1>Enter your Zip Code</h1>
            {query.changeZip === 'error' && (
              <article className="message is-danger">
                <div className="message-body">
                  We had a problem verifying this zip code. Please enter a valid
                  5-digit zip code.
                </div>
              </article>
            )}
            <QV
              id={query.quoteId}
              vehicleId={query.vehicleId || params.id}
              valueKey="customerZipCode"
              permit="customize"
            />
          </ModalBody>
          <ModalSubmitSet onSave={closeQuoteModal} />
        </Modal>
      )}

      {query.showInventoryFilters && (
        <Modal
          noClose
          className={c('auto-width inventory-filter-modal', {
            'power-search': !query.filterGroup,
          })}
          onClose={closeFiltersModal}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={closeFiltersModal}
            />
            {!query.filterGroup && FiltersCloseSection(closeFiltersModal)}
            <InventoryFilters
              group={query.filterGroup}
              closeFiltersModal={closeFiltersModal}
            />
            {FiltersCloseSection(closeFiltersModal)}
          </ModalBody>
        </Modal>
      )}

      {(query.addUser || query.editUserId) && (
        <Modal
          noClose
          className={c('auto-width edit-user-modal')}
          onClose={closeUserModal}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={closeUserModal}
            />
            <EditUserForm id={query.editUserId} onCancel={closeUserModal} />
          </ModalBody>
        </Modal>
      )}

      {false && query.customizeDemo && (
        <Modal
          className={c('auto-width edit-user-modal')}
          onClose={() => r(['customizeDemo', 'quoteAction'])}
          noClose={!query.customizeDemo}
        >
          <ModalBody>
            <SalesLeadForm />
          </ModalBody>
        </Modal>
      )}

      {query.paymentFinder && (
        <Modal
          noClose
          className={c('auto-width payment-finder-modal inside-close')}
          onClose={() => r(['paymentFinder'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['paymentFinder'])}
            />
            <Slider
              vehicleId={query.vehicleId || params.id}
              quoteId={query.quoteId}
              {...query}
            />
          </ModalBody>
        </Modal>
      )}

      {query.disclaimer && (
        <Modal
          noClose
          className={c('auto-width disclaimer-modal inside-close')}
          onClose={() => r(['disclaimer'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['disclaimer'])}
            />
            <ProgramDetails
              vehicleId={query.vehicleId || params.id}
              {...query}
            />
          </ModalBody>
        </Modal>
      )}

      {query.vehicleConfig && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['vehicleConfig'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['vehicleConfig'])}
            />
            <VehicleSettings
              id={query.vehicleConfig}
              onClose={() => r(['vehicleConfig'])}
            />
          </ModalBody>
        </Modal>
      )}

      {query.bankConfig && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['bankConfig'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['bankConfig'])}
            />
            <BankSettings
              id={query.bankConfig}
              onClose={() => r(['bankConfig'])}
            />
          </ModalBody>
        </Modal>
      )}

      {query.rooftopConfig && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['rooftopConfig'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['rooftopConfig'])}
            />
          </ModalBody>
        </Modal>
      )}

      {query.magicLinker && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['magicLinker'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['magicLinker'])}
            />
            <Suspense fallback={<Loading />}>
              <MagicLinker onCancel={() => r(['magicLinker'])} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.scripts && (
        <Modal
          noClose
          className={c('auto-width config-modal scripts-modal')}
          onClose={() => r(['scripts'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['scripts'])}
            />
            <Suspense fallback={<Loading />}>
              <Scripts onCancel={() => r(['scripts'])}>
                {doSave => (
                  <ModalSubmitSet
                    saveLabel="Save Scripts"
                    onSave={doSave}
                    onCancel={() => r(['scripts'])}
                  />
                )}
              </Scripts>
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.showAndTell && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['showAndTell'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['showAndTell'])}
            />
            <Suspense fallback={<Loading />}>
              <ShowAndTell onCancel={() => r(['showAndTell'])} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.paymentIQ && (
        <Modal
          noClose
          className={c('config-modal paymentIQ-modal')}
          onClose={() => r(['paymentIQ'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['paymentIQ'])}
            />
            <Suspense fallback={<Loading />}>
              <PaymentIQ onCancel={() => r(['paymentIQ'])} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.integrationSettings && (
        <Modal
          noClose
          className={c('auto-width config-modal')}
          onClose={() => r(['integrationSettings'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['integrationSettings'])}
            />
            <Suspense fallback={<Loading />}>
              <Integrations />
            </Suspense>
          </ModalBody>
        </Modal>
      )}

      {query.sessionId && (
        <Modal
          noClose
          className={c('auto-width lead-details-modal')}
          onClose={() => r(['sessionId'])}
        >
          <ModalBody>
            <ModalClose
              classaName="filter-close-button"
              onClose={() => r(['sessionId'])}
            />
            <Suspense fallback={<Loading />}>
              <LeadDetails id={query.sessionId} />
            </Suspense>
          </ModalBody>
        </Modal>
      )}
    </div>
  )
}

function mapStateToProps(state, op) {
  return {
    ...op,
    query: getActiveQuery(state),
    params: getActiveParams(state),
  }
}

export default connect(
  mapStateToProps,
  {
    toggleQueryValue,
    removeFromPageState,
    getVehiclesForVLP,
    deleteConfig,
    getProgramsForQuote,
  },
)(Modals)
